import * as React from 'react';

import { useRequest } from 'ahooks';
import {
  Alert,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Typography,
} from 'antd';
import dayjs from 'dayjs';

import {
  AntdDateRangePicker,
  CheckInputbox,
  DecimalInputNumber,
  ExclamationCircleOutlinedIcon,
  InboxOutlinedIcon,
  PrimitiveTreeSelect,
} from 'components';
import { useDiscountCodeAction, useDiscountCodeValue } from 'context';
import { getBrands, getCategories, searchProducts } from 'services';
import type { DiscountCodeType } from 'types';
import {
  generateSelectOptions,
  generateTreeSelectData,
  inputRule,
  localizeDiscountCodeType,
  normalizeToString,
  PATHNAME,
  UNIT,
} from 'utils';

const {
  QUERY_PARAM: {
    DEFAULT: {
      SORT_TYPE: DEFAULT_SORT_TYPE,
      PAGE_NUMBER: DEFAULT_PAGE_NUMBER,
      MAX_PAGE_SIZE: DEFAULT_MAX_PAGE_SIZE,
    },
  },
} = PATHNAME;
const { PRICE } = UNIT;

const discountCodeTypes = ['Percentage', 'Price'] satisfies DiscountCodeType[];

type ProductsParams = Parameters<typeof searchProducts>[number];

type FieldType = Pick<
  ReturnType<typeof useDiscountCodeValue>,
  | 'code'
  | 'type'
  | 'amount'
  | 'minCartPrice'
  | 'maxDiscountPrice'
  | 'duration'
  | 'isOnlyForFirstTime'
  | 'products'
  | 'brands'
  | 'categories'
>;

const DiscountCodeFormContent = () => {
  const {
    code,
    type,
    minCartPrice,
    maxDiscountPrice,
    duration,
    isOnlyForFirstTime,
    products: initialProducts = [],
    brands: initialBrands = [],
    categories: initialCategories = [],
  } = useDiscountCodeValue();
  const { data: categoriesData, loading: categoriesLoading } = useRequest(
    getCategories,
    {
      cacheKey: 'categories',
    },
  );
  const { data: brandsData, loading: brandsLoading } = useRequest(getBrands, {
    cacheKey: 'brands',
  });
  const {
    data: productsData,
    error: productsError,
    params: productsParams,
    loading: isProductsLoading,
    run: searchProductsRun,
  } = useRequest(
    async ({ searchKey }: Pick<ProductsParams, 'searchKey'>) =>
      await searchProducts({
        searchKey,
        sortType: DEFAULT_SORT_TYPE,
        pageNumber: DEFAULT_PAGE_NUMBER,
        pageSize: DEFAULT_MAX_PAGE_SIZE,
      }),
    {
      defaultParams: [{ searchKey: '' }],
      debounceWait: 1000,
    },
  );
  const action = useDiscountCodeAction();

  const { label: typeLabel } = localizeDiscountCodeType(type);
  const { startTime: initialStartTime, endTime: initialEndTime } =
    duration ?? {};

  const { categories = [] } = categoriesData ?? {};
  const { brands = [] } = brandsData ?? {};
  const { total: totalProducts = 0, data: products = [] } = productsData ?? {};
  const { searchKey: currentSearchKey = '' } = productsParams.at(0) ?? {};

  const categoriesTreeData = generateTreeSelectData(
    categories,
    'id',
    'title',
    'id',
  );
  const brandsSelectOptions = generateSelectOptions(
    brands,
    'id',
    'title',
    'id',
  );
  const productsSelectOptions = generateSelectOptions(
    products,
    'id',
    'name',
    'id',
  );

  return (
    <Row className='px-6 py-8'>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='شناسه'
          name='code'
          rules={[inputRule.required, inputRule.nonWhitespace]}
          initialValue={code}
        >
          <Input placeholder='شناسه کد تخفیف را وارد کنید' />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='ویژه خرید اولی‌ها'
          name='isOnlyForFirstTime'
          valuePropName='checked'
          initialValue={isOnlyForFirstTime}
        >
          <CheckInputbox>فقط ویژه خرید اولی‌ها</CheckInputbox>
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='نوع'
          name='type'
          rules={[inputRule.required]}
          initialValue={type}
        >
          <Select<DiscountCodeType | undefined>
            allowClear
            placeholder='نوع کد تخفیف را انتخاب کنید'
            options={discountCodeTypes.map(type => {
              const { label } = localizeDiscountCodeType(type);
              return {
                label,
                value: type,
              };
            })}
            onChange={value => {
              action({
                type: 'UPDATE_DISCOUNT_CODE_TYPE',
                payload: {
                  type: value,
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='مقدار'
          name='amount'
          rules={[inputRule.required, inputRule.digit]}
        >
          <DecimalInputNumber
            disabled={!type}
            addonAfter={typeLabel}
            max={type === 'Percentage' ? 100 : undefined}
            placeholder='مقدار کد تخفیف را وارد کنید'
            onChange={value => {
              if (value === null) return;

              action({
                type: 'UPDATE_DISCOUNT_CODE_AMOUNT',
                payload: {
                  amount: Number(value),
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='حداقل مبلغ خرید'
          name='minCartPrice'
          rules={[inputRule.digit]}
          initialValue={minCartPrice}
        >
          <DecimalInputNumber
            placeholder='حداقل مبلغ خرید کد تخفیف را وارد کنید'
            addonAfter={PRICE}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='حداکثر مقدار تخفیف'
          name='maxDiscountPrice'
          rules={[inputRule.digit]}
          initialValue={maxDiscountPrice}
        >
          <DecimalInputNumber
            placeholder='حداکثر مقدار تخفیف کد تخفیف را وارد کنید'
            addonAfter={PRICE}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='بازه زمانی'
          name='duration'
          rules={[inputRule.required]}
          initialValue={
            initialStartTime && initialEndTime
              ? [dayjs(initialStartTime), dayjs(initialEndTime)]
              : undefined
          }
        >
          <AntdDateRangePicker
            placeholder={['تاریخ شروع کد تخفیف', 'تاریخ پایان کد تخفیف']}
            onDateChange={([startTime, endTime]) => {
              if (!startTime || !endTime) return;

              action({
                type: 'UPDATE_DISCOUNT_CODE_DURATION',
                payload: {
                  duration: {
                    startTime,
                    endTime,
                  },
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Divider dashed className='mb-4 mt-0' />
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='برندها'
          name='brands'
          initialValue={initialBrands.map(({ id, title }) => ({
            key: id,
            label: title,
            value: id,
          }))}
        >
          <Select
            allowClear
            labelInValue
            mode='multiple'
            placeholder='برندهای کد تخفیف را انتخاب کنید'
            loading={brandsLoading}
            options={brandsSelectOptions}
            filterOption={(inputValue, option) =>
              normalizeToString(option?.label)
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='دسته‌بندی‌ها'
          name='categories'
          initialValue={initialCategories.map(({ id, title }) => ({
            key: id,
            label: title,
            value: id,
          }))}
        >
          <PrimitiveTreeSelect
            multiple
            treeCheckable
            treeCheckStrictly
            placeholder='دسته‌بندی‌های کد تخفیف را انتخاب کنید'
            loading={categoriesLoading}
            treeData={categoriesTreeData}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='محصول‌ها'
          name='products'
          initialValue={initialProducts.map(({ id, name }) => ({
            key: id,
            label: name,
            value: id,
          }))}
        >
          <Select
            allowClear
            labelInValue
            filterOption={false}
            popupMatchSelectWidth={false}
            mode='multiple'
            placeholder='محصول‌های کد تخفیف را انتخاب کنید'
            options={productsSelectOptions}
            notFoundContent={
              isProductsLoading ? (
                <div className='grid min-h-24 place-content-center gap-4'>
                  <Spin />
                  <Typography.Text className='text-[--ant-color-info]'>
                    در حال بارگذاری محصول‌ها...
                  </Typography.Text>
                </div>
              ) : productsError ? (
                <div className='grid min-h-24 content-center justify-items-center gap-4'>
                  <ExclamationCircleOutlinedIcon className='text-2xl text-[--ant-color-error]' />
                  <Typography.Text type='danger'>
                    {productsError instanceof Error
                      ? productsError.message
                      : String(productsError)}
                  </Typography.Text>
                </div>
              ) : !totalProducts ? (
                <div className='grid min-h-24 content-center justify-items-center gap-2.5'>
                  <InboxOutlinedIcon className='text-3xl' />
                  <Typography.Text type='secondary'>
                    <span>هیچ محصولی با عنوان</span>
                    <span> &rdquo;{currentSearchKey}&rdquo; </span>
                    <span>وجود ندارد.</span>
                  </Typography.Text>
                </div>
              ) : undefined
            }
            onSearch={value => {
              const searchKey = value.trim();
              searchProductsRun({ searchKey });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Flex vertical gap='small'>
          <Alert
            showIcon
            type='warning'
            message='تخفیف نهایی، بر اشتراک کلیه گزینه‌های انتخاب‌شده (برندها، دسته‌بندی‌ها و محصول‌ها)، محاسبه و اعمال می‌گردد.'
          />
        </Flex>
      </Col>
    </Row>
  );
};

export { DiscountCodeFormContent };
